(() => {
  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const ownerWindow = (element) => element?.ownerDocument?.defaultView || window;

  const isVisible = (element) => {
    if (!element || !element.getBoundingClientRect) return false;
    try {
      const win = ownerWindow(element);
      const style = win.getComputedStyle(element);
      if (!style || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
      if (Number.parseFloat(style.opacity || '1') <= 0.01) return false;
      if (style.pointerEvents === 'none') return false;
      if (element.hidden || element.getAttribute?.('aria-hidden') === 'true') return false;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 2 || rect.height <= 2) return false;
      const vw = win.innerWidth || document.documentElement.clientWidth || 0;
      const vh = win.innerHeight || document.documentElement.clientHeight || 0;
      if (vw && vh && (rect.bottom <= 0 || rect.right <= 0 || rect.top >= vh || rect.left >= vw)) return false;
      return true;
    } catch (_) {
      return false;
    }
  };

  const clickOnce = (element) => {
    if (!element || element.disabled) return false;
    try {
      element.click();
      return true;
    } catch (_) {
      try {
        element.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          composed: true,
          cancelable: true,
          view: ownerWindow(element)
        }));
        return true;
      } catch (_) {
        return false;
      }
    }
  };

  const queryRoots = (host) => [host?.shadowRoot, host].filter(Boolean);

  // MuFod v2.8.158 — product inquiry email record with Shopify hCaptcha protection.
  // Shopify Inbox remains the visible chat UI. On the same Contact Us click we
  // also submit the shopper's current product context through a hidden native
  // Shopify contact form, which sends the record to the store Sender email.
  const sentInquiryKeys = new Set();
  const pendingInquiryKeys = new Set();

  const compactText = (value = '') => String(value).replace(/\s+/g, ' ').trim();

  const getInquiryForm = () => document.getElementById('MufodAutoInquiryForm');

  const setInquiryField = (form, key, value) => {
    const field = form?.querySelector?.(`[data-inquiry-field="${key}"]`);
    if (field) field.value = value == null ? '' : String(value);
  };

  const selectedOptionLines = (form) => {
    if (!form?.querySelectorAll) return [];
    const lines = [];
    for (const fieldset of form.querySelectorAll('.mufod-pdp-option')) {
      const legend = fieldset.querySelector('legend');
      const selectedNode = fieldset.querySelector('[data-option-selected]');
      let value = compactText(selectedNode?.textContent || '');
      if (!value) {
        value = compactText(fieldset.querySelector('.mufod-pdp-option-value.is-active')?.dataset?.optionValue || '');
      }
      if (!value) continue;

      let label = compactText(legend?.textContent || 'Option');
      if (value && label.toLowerCase().endsWith(value.toLowerCase())) {
        label = compactText(label.slice(0, Math.max(0, label.length - value.length)));
      }
      label = label.replace(/[:：\-–—\s]+$/g, '').trim();
      label = label.replace(/^choose\s+/i, '').replace(/^pick\s+your\s+/i, '').trim();
      if (!label) label = 'Option';
      lines.push(`${label}: ${value}`);
    }
    return lines;
  };

  const buildInquiry = (button) => {
    const form = button?.closest?.('form') || null;
    const productRoot = button?.closest?.('[data-mufod-product]') || null;
    const isMainProductForm = Boolean(form?.matches?.('[data-product-form]'));

    const product = compactText(
      button?.dataset?.contactProductTitle ||
      productRoot?.querySelector?.('.mufod-pdp-title')?.textContent ||
      document.querySelector?.('h1')?.textContent ||
      document.title
    );

    const variant = compactText(
      form?.querySelector?.('[name="id"]')?.value ||
      productRoot?.querySelector?.('[data-variant-select]')?.value ||
      ''
    );

    const quantity = compactText(form?.querySelector?.('[name="quantity"]')?.value || '1') || '1';
    const optionLines = selectedOptionLines(form);

    const activePack = form?.querySelector?.('.mufod-pdp-deal.is-active') || null;
    const pack = compactText(
      activePack?.dataset?.packLabel ||
      activePack?.querySelector?.('[data-pack-title]')?.textContent ||
      form?.querySelector?.('[data-selected-deal-label]')?.textContent ||
      ''
    );

    const price = compactText(
      activePack?.querySelector?.('[data-pack-total]')?.textContent ||
      productRoot?.querySelector?.('[data-current-price]')?.textContent ||
      ''
    );

    const currency = compactText(
      productRoot?.dataset?.currency ||
      window.Shopify?.currency?.active ||
      ''
    );

    const market = compactText(
      window.Shopify?.country ||
      document.documentElement?.getAttribute?.('data-country') ||
      ''
    );

    let productUrl;
    try {
      if (isMainProductForm) {
        productUrl = new URL(window.location.href);
      } else {
        productUrl = new URL(button?.dataset?.contactProductUrl || productRoot?.dataset?.productUrl || window.location.href, window.location.origin);
      }
      if (variant) productUrl.searchParams.set('variant', variant);
      productUrl.hash = '';
    } catch (_) {
      productUrl = { href: window.location.href };
    }

    const pageUrl = window.location.href.split('#')[0];
    const submittedAt = new Date().toISOString();
    const inquiryId = `MF-${Date.now()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const specs = optionLines.join(' | ');

    const bodyLines = [
      'PRODUCT INQUIRY — Contact Us',
      `Inquiry ID: ${inquiryId}`,
      `Product: ${product || 'Unknown product'}`,
      specs ? `Selected specifications: ${specs}` : '',
      pack ? `Pack: ${pack}` : '',
      `Quantity: ${quantity}`,
      variant ? `Variant ID: ${variant}` : '',
      price ? `Price: ${price}${currency ? ` ${currency}` : ''}` : '',
      market ? `Market / Country: ${market}` : '',
      `Product URL: ${productUrl.href}`,
      `Page URL: ${pageUrl}`,
      `Submitted at: ${submittedAt}`,
      'Source: Contact Us button'
    ].filter(Boolean);

    const dedupeKey = [productUrl.href, variant, specs, pack, quantity].join('||');

    return {
      id: inquiryId,
      product,
      specs,
      pack,
      quantity,
      variant,
      price,
      currency,
      market,
      product_url: productUrl.href,
      page_url: pageUrl,
      submitted_at: submittedAt,
      body: bodyLines.join('\n'),
      dedupeKey
    };
  };

  const submitInquiry = (button) => {
    const form = getInquiryForm();
    if (!form) return false;

    const inquiry = buildInquiry(button);
    if (!inquiry?.dedupeKey) return false;
    if (sentInquiryKeys.has(inquiry.dedupeKey) || pendingInquiryKeys.has(inquiry.dedupeKey)) return true;

    // Keep the native Shopify form submission off-screen so the product page
    // and Inbox remain untouched. The iframe target is declared globally.
    form.setAttribute('target', 'MufodInquiryFrame');
    Object.entries(inquiry).forEach(([key, value]) => {
      if (key !== 'dedupeKey') setInquiryField(form, key, value);
    });

    const completeSubmit = () => {
      try {
        // Shopify's official CAPTCHA guidance for programmatic contact-form
        // submissions calls form.submit() only after captcha.protect is ready.
        form.submit();
        pendingInquiryKeys.delete(inquiry.dedupeKey);
        sentInquiryKeys.add(inquiry.dedupeKey);
        return true;
      } catch (_) {
        pendingInquiryKeys.delete(inquiry.dedupeKey);
        return false;
      }
    };

    pendingInquiryKeys.add(inquiry.dedupeKey);

    try {
      const protect = window.Shopify?.captcha?.protect;
      if (typeof protect === 'function') {
        protect(form, completeSubmit);
        return true;
      }

      // data-shopify-captcha="true" on the Liquid form wires hCaptcha at page
      // load. requestSubmit() is the safest fallback if the public helper is
      // temporarily unavailable when the shopper clicks Contact Us.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
        pendingInquiryKeys.delete(inquiry.dedupeKey);
        sentInquiryKeys.add(inquiry.dedupeKey);
        return true;
      }

      return completeSubmit();
    } catch (_) {
      pendingInquiryKeys.delete(inquiry.dedupeKey);
      return false;
    }
  };

  const launcherSelectors = [
    '[data-spec="toggle-button"]',
    'button.chat-toggle',
    '.chat-toggle',
    '.chat-app > button',
    '.activator',
    '.chat-toggle--bottom-right',
    '.chat-toggle--bottom-left',
    'button[aria-label*="chat" i]',
    'button[aria-label*="message" i]',
    'button[aria-label*="contact" i]',
    '[role="button"][aria-label*="chat" i]',
    '[role="button"][aria-label*="message" i]'
  ];

  const openControlSelectors = [
    '.chat-app--close-button',
    '[data-spec="close-button"]',
    'button[name="backButton"]',
    'button[aria-label*="close" i]',
    'button[aria-label*="minimize" i]',
    'button[aria-label*="minimise" i]',
    '[role="dialog"][open]',
    '[data-spec="chat-window"]'
  ];

  const findVisibleBySelectors = (root, selectors) => {
    if (!root?.querySelectorAll) return null;
    for (const selector of selectors) {
      for (const element of root.querySelectorAll(selector)) {
        if (element.matches?.('[data-contact-us]')) continue;
        if (isVisible(element)) return element;
      }
    }
    return null;
  };

  const findVisibleLauncher = (host) => {
    for (const root of queryRoots(host)) {
      const launcher = findVisibleBySelectors(root, launcherSelectors);
      if (launcher) return launcher;
    }
    return null;
  };

  const hasVisibleOpenControl = (host) => {
    for (const root of queryRoots(host)) {
      if (findVisibleBySelectors(root, openControlSelectors)) return true;
    }
    return false;
  };

  const getLegacyHosts = () => Array.from(document.querySelectorAll(
    'inbox-online-store-chat#ShopifyChat, inbox-online-store-chat, #ShopifyChat'
  )).filter((host, index, list) => list.indexOf(host) === index);

  const getModernHosts = () => Array.from(document.querySelectorAll('shopify-chat'));

  const legacyState = (host) => {
    if (!host) return null;

    const attr = String(host.getAttribute?.('is-open') || host.getAttribute?.('isopen') || '').toLowerCase();
    if (attr === 'true' || attr === 'open') return true;

    const launcher = findVisibleLauncher(host);
    const expanded = String(launcher?.getAttribute?.('aria-expanded') || '').toLowerCase();
    if (expanded === 'true') return true;

    if (hasVisibleOpenControl(host)) return true;

    // A clearly visible Inbox activator means the panel is closed/minimized.
    if (launcher) return false;

    if (attr === 'false' || attr === 'closed') return false;
    return null;
  };

  const modernState = (host) => {
    if (!host) return null;
    if (host.hasAttribute?.('open')) return true;
    if (hasVisibleOpenControl(host)) return true;

    const launcher = findVisibleLauncher(host);
    const expanded = String(launcher?.getAttribute?.('aria-expanded') || '').toLowerCase();
    if (expanded === 'true') return true;
    if (launcher) return false;

    return null;
  };

  // Local OPEN-ONLY latch. Shopify Inbox's internal state markers have changed
  // across versions. Once Contact Us opens Inbox, repeated Contact Us clicks are
  // ignored until a closed/minimized native launcher is visibly present again.
  let openLatch = false;
  let openedAt = 0;

  const nativeClosedLauncherVisible = () => {
    for (const host of getLegacyHosts()) {
      if (legacyState(host) === false && findVisibleLauncher(host)) return true;
    }
    for (const host of getModernHosts()) {
      if (modernState(host) === false && findVisibleLauncher(host)) return true;
    }
    return false;
  };

  const anyChatOpen = () => {
    for (const host of getModernHosts()) {
      if (modernState(host) === true) return true;
    }
    for (const host of getLegacyHosts()) {
      if (legacyState(host) === true) return true;
    }
    return false;
  };

  const refreshLatch = () => {
    if (!openLatch) return;
    // Do not let the launcher's closing animation immediately clear the latch.
    if (Date.now() - openedAt < 900) return;
    if (nativeClosedLauncherVisible()) openLatch = false;
  };

  const openModern = () => {
    for (const host of getModernHosts()) {
      const state = modernState(host);
      if (state === true) {
        openLatch = true;
        return true;
      }

      // Current Shopify storefront component exposes show() and reflects the
      // open state with the `open` attribute. Prefer this non-toggle API.
      if (typeof host.show === 'function') {
        try {
          host.show();
          openLatch = true;
          openedAt = Date.now();
          return true;
        } catch (_) {}
      }

      const launcher = findVisibleLauncher(host);
      if (launcher && clickOnce(launcher)) {
        openLatch = true;
        openedAt = Date.now();
        return true;
      }

      // Last-resort support for attribute-driven versions of <shopify-chat>.
      if (window.customElements?.get?.('shopify-chat')) {
        try {
          host.setAttribute('open', '');
          openLatch = true;
          openedAt = Date.now();
          return true;
        } catch (_) {}
      }
    }
    return false;
  };

  const openLegacy = () => {
    for (const host of getLegacyHosts()) {
      const state = legacyState(host);
      if (state === true) {
        openLatch = true;
        return true;
      }

      // OPEN-ONLY: click only a launcher that is visibly on screen. Hidden
      // toggle buttons inside an already-open panel are never touched.
      const launcher = findVisibleLauncher(host);
      if (launcher && clickOnce(launcher)) {
        openLatch = true;
        openedAt = Date.now();
        return true;
      }
    }
    return false;
  };

  const getDummyFrames = () => {
    const selectors = [
      '#dummy-chat-button-iframe',
      '#shopify-chat-dummy iframe',
      '#shopify-chat iframe',
      'iframe[id*="dummy-chat"]',
      'iframe[title*="chat" i]',
      'iframe[title*="message" i]'
    ];
    const result = [];
    for (const selector of selectors) {
      for (const frame of document.querySelectorAll?.(selector) || []) {
        if (!result.includes(frame)) result.push(frame);
      }
    }
    return result;
  };

  const openDummy = () => {
    for (const frame of getDummyFrames()) {
      try {
        const doc = frame.contentDocument || frame.contentWindow?.document;
        if (!doc) continue;
        const button = doc.getElementById('dummy-chat-button') ||
          Array.from(doc.querySelectorAll('button')).find(isVisible);
        if (button && clickOnce(button)) {
          openLatch = true;
          openedAt = Date.now();
          return true;
        }
      } catch (_) {
        // Cross-origin placeholders cannot be controlled from theme JavaScript.
      }
    }
    return false;
  };

  const openInbox = async () => {
    refreshLatch();

    if (anyChatOpen()) {
      openLatch = true;
      return true;
    }

    // If our previous Contact Us click opened the panel and it has not been
    // conclusively minimized yet, do nothing. This is what prevents Contact Us
    // from ever toggling an open Inbox closed.
    if (openLatch) return true;

    // The App Embed mounts asynchronously. Retry only until one OPEN action is
    // accepted; never re-click a toggle during the same request.
    for (let attempt = 0; attempt < 28; attempt += 1) {
      if (openModern()) return true;
      if (openLegacy()) return true;
      if (openDummy()) return true;
      await sleep(100);
    }
    return false;
  };

  const ensureToast = () => {
    let toast = document.querySelector('[data-mufod-contact-toast]');
    if (toast) return toast;
    toast = document.createElement('div');
    toast.setAttribute('data-mufod-contact-toast', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:24px',
      'transform:translateX(-50%) translateY(16px)',
      'z-index:2147483647',
      'max-width:min(92vw,560px)',
      'padding:12px 16px',
      'border-radius:10px',
      'background:#111',
      'color:#fff',
      'font:600 14px/1.4 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
      'box-shadow:0 10px 30px rgba(0,0,0,.22)',
      'opacity:0',
      'pointer-events:none',
      'transition:opacity .18s ease,transform .18s ease'
    ].join(';');
    document.body.appendChild(toast);
    return toast;
  };

  let toastTimer = 0;
  const showToast = (message, duration = 4200) => {
    const toast = ensureToast();
    toast.textContent = message;
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(-50%) translateY(16px)';
    }, duration);
  };

  // Track native Inbox interactions too, so opening/closing via Shopify's own
  // floating button stays in sync with our OPEN-ONLY latch.
  document.addEventListener('shopify-chat:close', () => { openLatch = false; }, true);
  document.addEventListener('shopify-chat:enter-standalone', () => { refreshLatch(); }, true);

  let opening = false;

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-contact-us]');
    if (!button) {
      // Shopify may auto-minimize when the shopper clicks elsewhere. On the next
      // interaction, clear our latch if the native closed launcher has returned.
      window.setTimeout(refreshLatch, 0);
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Fire the inquiry record independently from Inbox. It never blocks or
    // changes the stable OPEN-ONLY chat behavior.
    submitInquiry(button);

    refreshLatch();
    if (anyChatOpen() || openLatch || opening) return;

    opening = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const opened = await openInbox();
      if (!opened) {
        // Only show this when no Shopify Inbox launcher/component could be
        // reached at all. A visible native Chat bubble will no longer produce
        // a false "unavailable" warning.
        showToast('Live chat is unavailable right now. Please email banjo@mufod.com.');
      }
    } finally {
      button.removeAttribute('aria-busy');
      opening = false;
    }
  });
})();
